import { Link, usePage } from "@inertiajs/react"
import { useEffect } from "react"
import { toast } from "sonner"
import {
  BarChart3,
  BookOpen,
  Building2,
  Calendar,
  ChevronDown,
  CreditCard,
  Globe,
  LayoutDashboard,
  LogOut,
  MapPin,
  Megaphone,
  MessageSquare,
  Moon,
  Package,
  Settings,
  Shield,
  Sparkles,
  Sun,
  Tag,
  Truck,
  UserRound,
  Users,
} from "lucide-react"
import type { ReactNode } from "react"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Toaster } from "@/components/ui/sonner"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import type { AccessibleProperty, NavUrls, SharedProps, UserContext } from "@/types"
import { useTheme } from "@/hooks/use-theme"

type NavItem = {
  label: string
  href: string
  activeHref?: string
  icon: React.ComponentType<{ className?: string }>
  exact?: boolean
}

function platformNav(urls: NavUrls): NavItem[] {
  return [
    { label: "Regions",      href: urls.regions,       icon: Globe },
    { label: "Categories",   href: urls.poi_categories, icon: Tag },
    { label: "Distributors", href: urls.distributors,   icon: Truck },
    { label: "Properties",    href: urls.properties,    icon: Building2 },
    { label: "Subscriptions", href: urls.subscriptions,  icon: CreditCard },
    { label: "Content",       href: urls.content,        icon: MapPin },
    { label: "Moderation",    href: urls.moderations,    icon: Shield },
    { label: "Analytics",     href: urls.analytics,      icon: BarChart3 },
    { label: "Two-Layer Lift", href: urls.two_layer_lift, icon: BarChart3 },
  ]
}

function distributionNav(urls: NavUrls): NavItem[] {
  return [
    { label: "My Regions",          href: urls.regions,    icon: Globe },
    { label: "Regional Properties", href: urls.properties, icon: Building2 },
    { label: "Regional Content",    href: urls.content,    icon: MapPin },
    { label: "Onboarding",          href: urls.onboard,    icon: Megaphone },
    { label: "Regional Moderation", href: urls.moderations, icon: Shield },
    { label: "Regional Analytics",  href: urls.analytics,  icon: BarChart3 },
  ]
}

function propertyNav(adminUrl: string, canManageGuests: boolean): NavItem[] {
  const items: NavItem[] = [
    { label: "Dashboard",   href: adminUrl,                  icon: LayoutDashboard, exact: true },
    { label: "INSIDE Info", href: `${adminUrl}/inside`,      icon: MessageSquare },
    { label: "POIs",        href: `${adminUrl}/pois`,        icon: MapPin },
    { label: "Events",      href: `${adminUrl}/events`,      icon: Calendar },
    { label: "Guides",      href: `${adminUrl}/guides`,      icon: Globe },
    { label: "Delivery",    href: `${adminUrl}/delivery`,    icon: Package },
    { label: "Team",        href: `${adminUrl}/team`,        icon: Users },
    { label: "Analytics",   href: `${adminUrl}/analytics`,   icon: BarChart3 },
    { label: "Settings",    href: `${adminUrl}/settings`,    icon: Settings },
  ]

  if (canManageGuests) {
    // Insert Guests after Team (index 6)
    items.splice(7, 0, { label: "Guests", href: `${adminUrl}/guest_profiles`, icon: UserRound })
  }

  return items
}

function regionNav(
  adminUrl: string,
  analyticsUrl: string,
  regionId: number,
  qualityUrl?: string,
): NavItem[] {
  const items: NavItem[] = [
    { label: "Overview",   href: adminUrl,                                icon: LayoutDashboard, exact: true },
    { label: "Properties", href: `${adminUrl}?tab=properties`,            icon: Building2 },
    { label: "Guide Studio", href: `${adminUrl}/studio`,                  icon: BookOpen },
  ]
  // T9: Quality tab visibility is gated by the server omitting `quality_url`
  // when the QUALITY_WORKBENCH_ENABLED flag is off. No client-side feature flag.
  if (qualityUrl) {
    items.push({ label: "Quality", href: qualityUrl, icon: Sparkles })
  }
  items.push(
    { label: "POIs",       href: `${adminUrl}/pois`,                      icon: MapPin },
    { label: "Events",     href: `${adminUrl}/events`,                    icon: Calendar },
    { label: "Analytics",  href: `${analyticsUrl}?region_id=${regionId}`, icon: BarChart3 },
  )
  return items
}

function isNavActive(currentUrl: string, item: NavItem): boolean {
  const matchHref = item.activeHref ?? item.href
  if (!matchHref) return false
  // Tab-param links (e.g. ?tab=properties): compare path + tab param independently
  // so extra params like ?tab=properties&page=2 still match
  if (matchHref.includes("?tab=")) {
    const tabParam = new URLSearchParams(matchHref.split("?")[1]).get("tab")
    const currentTab = new URLSearchParams(currentUrl.split("?")[1] ?? "").get("tab")
    return currentUrl.split("?")[0] === matchHref.split("?")[0] && currentTab === tabParam
  }
  if (item.exact) return currentUrl === matchHref
  return currentUrl === matchHref || currentUrl.startsWith(matchHref + "/")
}

function NavItems({ items, currentUrl }: { items: NavItem[]; currentUrl: string }) {
  return (
    <SidebarMenu>
      {items.map((item) => (
        <SidebarMenuItem key={item.href + item.label}>
          <SidebarMenuButton asChild isActive={isNavActive(currentUrl, item)}>
            <Link href={item.href}>
              <item.icon className="size-4" />
              <span>{item.label}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  )
}

function PropertySection({
  properties,
  currentUrl,
  ctx,
}: {
  properties: AccessibleProperty[]
  currentUrl: string
  ctx: UserContext
}) {
  const activeProperty =
    properties.find((p) => currentUrl === p.admin_url || currentUrl.startsWith(p.admin_url + "/"))
    ?? properties[0]

  if (!activeProperty) return null

  // Guests nav is visible for owners and managers (not staff-only members)
  // Show Guests nav to: platform admins, support, owners, managers, and staff
  // (policy enforces read-only for staff; all roles can navigate to the section)
  const canManageGuests =
    ctx.is_admin ||
    ctx.is_support ||
    ctx.owned_property_ids.includes(activeProperty.id) ||
    ctx.member_property_ids.includes(activeProperty.id)

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Property</SidebarGroupLabel>
      <SidebarGroupContent>
        {properties.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="mb-1 w-full justify-between px-2 font-medium"
              >
                <span className="truncate">{activeProperty.name}</span>
                <ChevronDown className="ml-1 size-3.5 shrink-0 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {properties.map((p) => (
                <DropdownMenuItem key={p.id} asChild>
                  <Link href={p.admin_url}>{p.name}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <p className="mb-1 truncate px-2 text-sm font-medium text-sidebar-foreground">
            {activeProperty.name}
          </p>
        )}
        <NavItems items={propertyNav(activeProperty.admin_url, canManageGuests)} currentUrl={currentUrl} />
      </SidebarGroupContent>
    </SidebarGroup>
  )
}

// NOTE: navUrls is passed explicitly so Analytics URL uses the server-provided
// nav_urls.analytics path (consistent with Distribution section pattern).
function RegionSection({
  ctx,
  currentUrl,
  navUrls,
}: {
  ctx: UserContext
  currentUrl: string
  navUrls: NavUrls
}) {
  const regions = ctx.managed_regions
  if (!regions.length) return null

  // Mirror PropertySection: always-visible with fallback to regions[0].
  // Guard `r.admin_url &&` prevents `startsWith("" + "/")` from activating
  // the section on every page when admin_url is empty/null.
  const activeRegion =
    regions.find((r) =>
      r.admin_url && (
        currentUrl === r.admin_url ||
        currentUrl.startsWith(r.admin_url + "/") ||
        currentUrl.startsWith(r.admin_url + "?")
      )
    )
    ?? regions[0]
    ?? null

  if (!activeRegion?.admin_url) return null

  const items = regionNav(
    activeRegion.admin_url,
    navUrls.analytics,
    activeRegion.id,
    activeRegion.quality_url,
  )

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Region</SidebarGroupLabel>
      <SidebarGroupContent>
        {regions.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="mb-1 w-full justify-between px-2 font-medium"
              >
                <span className="truncate">{activeRegion.name}</span>
                <ChevronDown className="ml-1 size-3.5 shrink-0 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 max-h-64 overflow-y-auto">
              {regions.map((r) => (
                <DropdownMenuItem key={r.id} asChild>
                  <Link href={r.admin_url}>{r.name}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <p className="mb-1 truncate px-2 text-sm font-medium text-sidebar-foreground">
            {activeRegion.name}
          </p>
        )}
        <NavItems items={items} currentUrl={currentUrl} />
      </SidebarGroupContent>
    </SidebarGroup>
  )
}

function userInitials(firstName: string | null, lastName: string | null, email: string) {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase()
  if (firstName) return firstName[0].toUpperCase()
  return email[0].toUpperCase()
}

function userDisplayName(
  firstName: string | null,
  lastName: string | null,
  email: string,
) {
  if (firstName || lastName) return [firstName, lastName].filter(Boolean).join(" ")
  return email
}

export function AdminLayout({ children }: { children: ReactNode }) {
  const { theme, setTheme } = useTheme()
  const { url, props } = usePage<SharedProps>()
  const { auth, user_context, accessible_properties, nav_urls, flash } = props

  useEffect(() => {
    if (flash?.notice) toast(flash.notice)
    if (flash?.alert) toast.error(flash.alert)
  }, [flash?.notice, flash?.alert])

  const ctx: UserContext = user_context ?? {
    is_admin: false,
    is_support: false,
    managed_region_ids: [],
    managed_regions: [],
    owned_property_ids: [],
    member_property_ids: [],
  }

  const properties = accessible_properties ?? []
  const showPlatform = ctx.is_admin || ctx.is_support
  const showDistribution = ctx.managed_region_ids.length > 0
  const showProperty = properties.length > 0

  const user = auth?.user
  const initials = user ? userInitials(user.first_name, user.last_name, user.email) : "?"
  const displayName = user
    ? userDisplayName(user.first_name, user.last_name, user.email)
    : ""
  const signOutPath = user ? `/sessions/${user.session_id}` : "/sign_in"

  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader>
          <div className="flex items-center justify-between">
            <Link
              href={nav_urls?.root ?? "/admin"}
              className="flex items-center gap-2 px-2 py-1 text-lg font-semibold tracking-tight text-sidebar-foreground"
            >
              INNLY
            </Link>
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="rounded-md p-1 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </button>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={url === (nav_urls?.root ?? "/admin")}>
                    <Link href={nav_urls?.root ?? "/admin"}>
                      <LayoutDashboard className="size-4" />
                      <span>Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {showPlatform && nav_urls && (
            <SidebarGroup>
              <SidebarGroupLabel>Platform</SidebarGroupLabel>
              <SidebarGroupContent>
                <NavItems items={platformNav(nav_urls)} currentUrl={url} />
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {showDistribution && nav_urls && (
            <SidebarGroup>
              <SidebarGroupLabel>Distribution</SidebarGroupLabel>
              <SidebarGroupContent>
                <NavItems items={distributionNav(nav_urls)} currentUrl={url} />
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {nav_urls && (
            <RegionSection ctx={ctx} currentUrl={url} navUrls={nav_urls} />
          )}

          {showProperty && (
            <PropertySection properties={properties} currentUrl={url} ctx={ctx} />
          )}
        </SidebarContent>

        <SidebarFooter>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-auto w-full justify-start gap-2 px-2 py-2"
              >
                <Avatar className="size-7">
                  <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                </Avatar>
                <span className="truncate text-sm font-medium">{displayName}</span>
                <ChevronDown className="ml-auto size-3.5 shrink-0 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-56">
              {user && (
                <>
                  <div className="px-2 py-1.5">
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem asChild>
                <Link
                  href={signOutPath}
                  method="delete"
                  as="button"
                  className="flex w-full items-center gap-2 text-destructive"
                >
                  <LogOut className="size-4" />
                  Sign out
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <header className="flex h-12 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
        </header>
        <main className="flex-1 p-6">{children}</main>
      </SidebarInset>
      <Toaster />
    </SidebarProvider>
  )
}
